import React, { FC, useContext, useMemo } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import {
  ConfigContext,
  RedirectionProvider,
  UppAuthContext,
  UserContext,
} from "@app/provider";
import PublicRouting from "./pages/public/routing";
import PrivateRouting from "./pages/private/routing";
import RedirectLPP, {
  billingPath,
  requestMobilePhlebotomyPath,
} from "./pages/public/redirectLPP/redirectLpp";
import { TestCardStatus } from "@app/provider/testData/types";
import { SecurityContext } from "@natera/platform/lib/provider/security";
import { LogOut } from "@app/pages";
import PageTitle from "@app/components/PageTitle/PageTitle";

const root = "";

const getProfileRoutes = () => {
  const personalInformation = `${root}/personal_information`;
  const updatePersonalInformation = `${personalInformation}/update`;
  const contactDetails = `${root}/contact_details`;
  const contactDetailsUpdatePhone = `${contactDetails}/update_phone`;
  const updateEmail = `${root}/update_email`;
  const contactDetailsCreateOrUpdateAddress = `${contactDetails}/create_or_update_address`;
  const securitySettings = `${root}/security_settings`;
  const changePassword = `${root}/change_password`;
  const communicationPreferences = `${root}/communication`;
  const updateCommunicationPreferences = `${communicationPreferences}/update`;

  return {
    personalInformation,
    updatePersonalInformation,
    contactDetails,
    contactDetailsUpdatePhone,
    updateEmail,
    contactDetailsCreateOrUpdateAddress,
    securitySettings,
    changePassword,
    communicationPreferences,
    updateCommunicationPreferences,
  };
};

export const profileRoutes = getProfileRoutes();

const getLegacyPpRoutes = () => {
  const legacyPpGeneticInfo = `${root}/services/genetic_information`;
  const legacyPpBloodDraw = `${root}/services/blood_draw`;
  const legacyPpCompassionateCare = `${root}/compassionate_care/form`;
  const legacyPpEmpower = `${root}/empower/*`;
  // NOTE: /education/* removed - handled in PublicRouting based on redirectEducation config
  const legacyPpPricingCompassionateCare = `${root}/pricing/about_compassionate_care`;
  const legacyPpPricingGeneticTesting = `${root}/pricing/genetic_testing`;
  const legacyPpPreferences = `${root}/preferences`;
  const legacyPpBilling = `${root}${billingPath}`;

  return [
    legacyPpGeneticInfo,
    legacyPpBloodDraw,
    legacyPpCompassionateCare,
    legacyPpEmpower,
    legacyPpPricingCompassionateCare,
    legacyPpPricingGeneticTesting,
    legacyPpPreferences,
    legacyPpBilling,
  ];
};

const legacyPpRoutes = getLegacyPpRoutes();

const getRoutes = () => {
  const rootPage = `${root}/`;
  const resetPasswordError = `${root}/reset_password_error`;
  const resetPassword = `${root}/reset_password`;
  const setPassword = `${root}/set_password`;
  const sendEmail = `${root}/send_email`;
  const logIn = `${root}/log_in`;
  const loginRedirect = `${root}/log_in_redirect`;
  const signUp = `${root}/sign_up`;
  const activation = `${root}/activation`;
  const token = `${root}/token/:token`;
  const home = `${root}/home`;
  const billing = `${root}/billing`;
  const account = `${root}/account`;
  const orders = `${root}/orders`;
  const education = `${root}/education`;
  const profile = `${root}/profile`;
  const help = `${root}/help`;
  const feedback = `${root}/feedback`;
  const setupProfile = `${root}/setup_profile`;
  const confirmation = `${root}/confirmation`;
  const loginInInvite = `${logIn}/:invite?`;
  const signUpInvite = `${signUp}/:invite?`;
  const promoLanding = `${root}/p/landing/:promo`;
  const updateEmailError = `${root}/update_email_error`;
  const resendUpdateEmail = `${root}/resend_update_email`;
  const updatePassword = `${root}/update_password`;
  const unlock = `${root}/unlock`;
  const scheduleBloodDraw = `${root}/schedule_blood_draw`;
  const drawRequest = `${root}/services/draw_request`;
  const displayVideo = `${root}/display_video`;
  const renewalInterested = `${root}/renewal_interested`;
  const test = `${root}/test/:orderUidPublic/:testUid`;
  const scheduleAGeneticInformationSession = `${root}/schedule_a_gis`;
  const scheduleAGeneticInformationSessionPatientEducation = `${root}/:orderUidPublic/:testUid/patient-education`;
  const scheduleAGeneticInformationSessionIntake = `${root}/:orderUidPublic/:testUid/intake`;
  const scheduleAGeneticInformationSessionPatientEducationPage = (
    orderUidPublic: string,
    testUid: string
  ) => `${root}/${orderUidPublic}/${testUid}/patient-education`;
  const scheduleAGeneticInformationSessionIntakePage = (
    orderUidPublic: string,
    testUid: string
  ) => `${root}/${orderUidPublic}/${testUid}/intake`;
  // Guest-only education content, reached from the GIS patient-education step.
  // The authenticated flow links to `understandingYourResults` instead, which
  // is backed by the patient's test result; this one is static per test type.
  const scheduleAGeneticInformationSessionUnderstandingResults = `${root}/:orderUidPublic/:testUid/understanding-your-results`;
  const scheduleAGeneticInformationSessionUnderstandingResultsPage = (
    orderUidPublic: string,
    testUid: string
  ) => `${root}/${orderUidPublic}/${testUid}/understanding-your-results`;
  const scheduleAGeneticInformationSessionSchedule = `${root}/:orderUidPublic/:testUid/schedule_session`;
  const scheduleAGeneticInformationSessionSchedulePage = (
    orderUidPublic: string,
    testUid: string
  ) => `${root}/${orderUidPublic}/${testUid}/schedule_session`;
  const testPage = (orderUidPublic: string, testUid: string) =>
    `${root}/test/${orderUidPublic}/${testUid}`;

  const testDetails = `${test}/details`;
  const testDetailsPage = (orderUidPublic: string, testUid: string) =>
    `${testPage(orderUidPublic, testUid)}/details`;

  const testDetailsInformation = `${testDetails}/information`;
  const testDetailsInformationPage = (
    orderUidPublic: string,
    testUid: string
  ) => `${testDetailsPage(orderUidPublic, testUid)}/information`;

  const testResultPdf = `${test}/result-pdf/:testDocumentUid`;
  const testResultPdfPage = (
    orderUidPublic: string,
    testUid: string,
    testDocumentUid: string
  ) => `${testPage(orderUidPublic, testUid)}/result-pdf/${testDocumentUid}`;

  const testResultDetails = `${test}/result-details`;
  const testResultDetailsPage = (orderUidPublic: string, testUid: string) =>
    `${testPage(orderUidPublic, testUid)}/result-details`;

  const shareFetalSex = `${test}/share-fetal-sex`;
  const shareFetalSexPage = (orderUidPublic: string, testUid: string) =>
    `${testPage(orderUidPublic, testUid)}/share-fetal-sex`;

  const supplementalPdf = `${test}/pdf/:uid`;
  const supplementalPdfPage = (
    orderUidPublic: string,
    testUid: string,
    uid: string
  ) => `${testPage(orderUidPublic, testUid)}/pdf/${uid}`;
  const supplementalVideo = `${test}/video/:uid`;
  const supplementalVideoPage = (
    orderUidPublic: string,
    testUid: string,
    uid: string
  ) => `${testPage(orderUidPublic, testUid)}/video/${uid}`;
  const understandingYourResults = `${test}/:testStatus/:testDocumentUid/understanding-your-results/`;
  const understandingYourResultsPage = (
    orderUidPublic: string,
    testUid: string,
    testStatus: TestCardStatus,
    testDocumentUid: string
  ) =>
    `${testPage(
      orderUidPublic,
      testUid
    )}/${testStatus}/${testDocumentUid}/understanding-your-results/`;
  const downloadableResultsGuide = `${test}/downloadable-results-guide/`;
  const downloadableResultsGuidePage = (
    orderUidPublic: string,
    testUid: string
  ) => `${testPage(orderUidPublic, testUid)}/downloadable-results-guide/`;
  const sampleDraw = `${root}/sample_draw/:orderUidPublic/:testUid`;
  const sampleDrawModify = `${sampleDraw}/modify`;
  const sampleDrawPage = (orderUidPublic: string, testUid: string) =>
    `${root}/sample_draw/${orderUidPublic}/${testUid}`;
  const sampleDrawModifyPage = (orderUidPublic: string, testUid: string) =>
    `${sampleDrawPage(orderUidPublic, testUid)}/modify`;
  const logOut = `${root}/log_out`;
  const simpleOrder = `${root}/simple_order/:orderUidPublic`;
  const simpleOrderPage = (orderUidPublic: string) =>
    `${root}/simple_order/${orderUidPublic}`;
  const simpleOrderAboutTest = `/about_test/:videoType`;
  const aboutTestPage = (orderUidPublic: string, videoType: string) =>
    `${root}/simple_order/${orderUidPublic}/about_test/${videoType}`;
  const testHistory = `${root}/test_history`;
  const guestLogin = `${root}/guest_login`;
  const billingEstimate = `${root}/billing_estimate`;
  const nonCoveredService = `${root}/non_covered_service`;
  const outOfNetwork = `${root}/out_of_network`;
  const priorAuthorization = `${root}/prior_authorization`;
  const noLongerAvailable = `${root}/no_longer_available`;
  const insuranceNoLongerAvailable = `${root}/insurance_no_longer_available`;
  const billingUpdates = `${root}/billing_updates`;
  // START OF POST-BILL PATHS
  // guest login page for postbill
  const postbillGuestLogin = `${root}/billing_guest_login`;
  // public login page for postbill
  const billingCenterGuestLogin = `${root}/payment`;
  const billingCenter = `${root}/billing_center`;
  const reviewBills = `${root}/billing_review`;
  const billingPayment = `${root}/billing_payment`;
  const billingFailure = `${root}/billing_failure`;
  const billingConfirmation = `${root}/billing_confirmation`;
  const billingCenterHistory = `${root}/billing_center_history`;
  const billingCenterHelp = `${root}/billing_center_help`;
  const billingHistoryStatements = `${root}/billing_history_statements`;
  const billingHistoryPayments = `${root}/billing_history_payments`;
  const billingReceiptPdf = `${root}/billing_receipt`;
  const billingStatementPdf = `${root}/billing_statement`;
  const billingCenterPaymentPlanHistory = `${root}/billing_history_payment_plans`;
  const billingPaymentPlanDetail = `${root}/billing_payment_plan_detail`;
  const billingUpdatePayment = `${root}/billing_update_payment`;
  const billingPaymentSuccess = `${root}/billing_payment_success`;
  // END OF POST-BILL PATHS
  const upsGuestLogin = `${root}/ups_guest_login`;
  const upsUpdatePaymentMethod = `${root}/update_payment_method`;
  const upsReceipt = `${root}/receipt`;
  const upsUpdatePaymentMethodSuccess = `${root}/update_payment_method_success`;
  const upsUpdatePaymentMethodFailed = `${root}/update_payment_method_failed`;
  const upsReviewInvoice = `${root}/review_invoice`;
  const upsCreatePaymentMethod = `${root}/create_payment_method`;
  const upsCreatePaymentConfirmation = `${root}/payment_confirmation`;
  const upsCreatePaymentFailure = `${root}/payment_failure`;
  const upsNoLongerAvailable = `${root}/payment_not_available`;

  // Public GIS entry + authenticated triage
  const publicGisEntry = `${root}/gis/oncology`;
  const gisTriage = `${root}/gis/triage`;
  const gisOrderSelect = `${root}/gis/select`;
  // Anonymous order-lookup (Public_Entry_Order_Lookup) guest funnel
  const publicGisOrderLookup = `${root}/gis/oncology/lookup`;

  // Prenatal Education Routes
  const patientEducation = `${root}/education/prenatal/:publicClinicId`;
  const patientEducationPage = (publicClinicId: string) =>
    `${root}/education/prenatal/${publicClinicId}`;
  const patientEducationPanorama = `${patientEducation}/panorama`;
  const patientEducationPanoramaPage = (publicClinicId: string) =>
    `${patientEducationPage(publicClinicId)}/panorama`;
  const patientEducationHorizon = `${patientEducation}/horizon`;
  const patientEducationHorizonPage = (publicClinicId: string) =>
    `${patientEducationPage(publicClinicId)}/horizon`;
  const patientEducationQuestionnaire = `${patientEducation}/questionnaire`;
  const patientEducationQuestionnairePage = (publicClinicId: string) =>
    `${patientEducationPage(publicClinicId)}/questionnaire`;
  const patientEducationCompletion = `${patientEducation}/thanks`;
  const patientEducationCompletionPage = (publicClinicId: string) =>
    `${patientEducationPage(publicClinicId)}/thanks`;

  return {
    root,
    rootPage,
    sendEmail,
    resetPassword,
    resetPasswordError,
    setPassword,
    logIn,
    loginRedirect,
    signUp,
    promoLanding,
    activation,
    token,
    home,
    billing,
    account,
    orders,
    education,
    profile,
    help,
    feedback,
    setupProfile,
    loginInInvite,
    signUpInvite,
    confirmation,
    updateEmailError,
    resendUpdateEmail,
    updatePassword,
    scheduleBloodDraw,
    drawRequest,
    unlock,
    displayVideo,
    renewalInterested,
    test,
    testPage,
    testDetails,
    testDetailsPage,
    testDetailsInformation,
    testDetailsInformationPage,
    testResultPdf,
    testResultPdfPage,
    testResultDetails,
    testResultDetailsPage,
    shareFetalSex,
    shareFetalSexPage,
    supplementalPdf,
    supplementalPdfPage,
    supplementalVideo,
    supplementalVideoPage,
    understandingYourResults,
    understandingYourResultsPage,
    downloadableResultsGuide,
    downloadableResultsGuidePage,
    logOut,
    sampleDraw,
    sampleDrawModify,
    sampleDrawPage,
    sampleDrawModifyPage,
    simpleOrder,
    simpleOrderPage,
    testHistory,
    simpleOrderAboutTest,
    aboutTestPage,
    scheduleAGeneticInformationSession,
    scheduleAGeneticInformationSessionPatientEducation,
    scheduleAGeneticInformationSessionPatientEducationPage,
    scheduleAGeneticInformationSessionUnderstandingResults,
    scheduleAGeneticInformationSessionUnderstandingResultsPage,
    scheduleAGeneticInformationSessionSchedule,
    scheduleAGeneticInformationSessionSchedulePage,
    scheduleAGeneticInformationSessionIntake,
    scheduleAGeneticInformationSessionIntakePage,
    billingEstimate,
    nonCoveredService,
    outOfNetwork,
    priorAuthorization,
    guestLogin,
    noLongerAvailable,
    insuranceNoLongerAvailable,
    billingUpdates,
    postbillGuestLogin,
    billingCenter,
    reviewBills,
    billingPayment,
    billingFailure,
    billingConfirmation,
    billingCenterHistory,
    billingCenterHelp,
    billingHistoryStatements,
    billingHistoryPayments,
    billingReceiptPdf,
    billingStatementPdf,
    billingCenterGuestLogin,
    billingCenterPaymentPlanHistory,
    billingPaymentPlanDetail,
    billingUpdatePayment,
    billingPaymentSuccess,
    upsGuestLogin,
    upsUpdatePaymentMethod,
    upsUpdatePaymentMethodSuccess,
    upsUpdatePaymentMethodFailed,
    upsReviewInvoice,
    upsCreatePaymentMethod,
    upsCreatePaymentConfirmation,
    upsCreatePaymentFailure,
    upsNoLongerAvailable,
    upsReceipt,
    publicGisEntry,
    gisTriage,
    gisOrderSelect,
    publicGisOrderLookup,
    patientEducation,
    patientEducationPage,
    patientEducationPanorama,
    patientEducationPanoramaPage,
    patientEducationHorizon,
    patientEducationHorizonPage,
    patientEducationQuestionnaire,
    patientEducationQuestionnairePage,
    patientEducationCompletion,
    patientEducationCompletionPage,
    ...profileRoutes,
  };
};

export const routes = getRoutes();

const Routing: FC = () => {
  const {
    signInByAuthenticationProviderInProgress:
      signUpByAuthenticationProviderInProgress,
    profile,
  } = useContext(UppAuthContext);
  const { uppUser } = useContext(UserContext);
  const { isAuthorized } = useContext(SecurityContext);
  const { config } = useContext(ConfigContext);

  const legacyPpRedirectionEnabled = config.patientPortal.redirectionEnabled;

  const isAuth = isAuthorized();
  const expiresAt = JSON.parse(
    localStorage.getItem("okta-token-patient") || "{}"
  )?.AUTH_TOKEN?.expiresAt;

  const isExpired = useMemo(
    () => !expiresAt || Date.now() / 1000 - expiresAt > 0,
    [isAuth, signUpByAuthenticationProviderInProgress]
  );

  const user = profile || uppUser;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LogOut />} path={routes.logOut} />

        {/* Always redirect to legacy PP: lets WH patients with paper orders
            (no order in SOS) reach the no-order blood-draw request / lab
            locator flow. Not gated by redirectionEnabled. */}
        <Route path={requestMobilePhlebotomyPath} element={<RedirectLPP />} />

        {legacyPpRedirectionEnabled &&
          legacyPpRoutes.map((path) => (
            <Route key={path} path={path} element={<RedirectLPP />} />
          ))}

        <Route
          path="*"
          element={
            <>
              <PageTitle />
              <RedirectionProvider>
                {!isExpired &&
                user &&
                !signUpByAuthenticationProviderInProgress ? (
                  <PrivateRouting />
                ) : (
                  <PublicRouting />
                )}
              </RedirectionProvider>
            </>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default Routing;
